// pages/SystemStatus.jsx
import React, { useState, useEffect } from 'react';
import { 
CheckCircle, AlertCircle, Clock, XCircle, Calendar, 
ArrowLeft, Bell, Mail, ChevronRight, Info, Shield, X
} from 'lucide-react';
import apiRequest from '@/services/requests';

const SystemStatus = () => {
const [status, setStatus] = useState(null);
const [loading, setLoading] = useState(true);
const [showNotifyModal, setShowNotifyModal] = useState(false);
const [notifyEmail, setNotifyEmail] = useState('');
const [notifySuccess, setNotifySuccess] = useState(false);
const [subscribing, setSubscribing] = useState(false);

useEffect(() => {
loadStatus();

// Refresh every 5 minutes
const interval = setInterval(loadStatus, 300000);
return () => clearInterval(interval);
}, []);

const loadStatus = async () => {
setLoading(true);
try {
const res = await apiRequest.getSystemStatus();
if (res.success) {
setStatus(res.data);
}
} catch (error) {
console.error('Error loading status:', error);
}
setLoading(false);
};

const handleNotifySubscribe = async (e) => {
e.preventDefault();

if (!notifyEmail.trim()) return;

setSubscribing(true);
try {
const res = await apiRequest.subscribeToStatusUpdates({ 
email: notifyEmail,
notification_type: 'email'
});

if (res.success) {
setNotifySuccess(true);
setTimeout(() => {
setShowNotifyModal(false);
setNotifySuccess(false);
setNotifyEmail('');
}, 2000);
}
} catch (error) {
console.error('Subscribe error:', error);
alert('Failed to subscribe. Please try again.');
}
setSubscribing(false);
};

const getStatusConfig = (statusType) => {
const configs = {
operational: {
icon: CheckCircle,
color: 'green',
bg: 'bg-green-50',
border: 'border-green-200',
text: 'text-green-700',
badge: 'bg-green-100 text-green-800',
label: '🟢 Operational',
description: 'All systems are running smoothly'
},
upgrade: {
icon: Clock,
color: 'amber',
bg: 'bg-amber-50',
border: 'border-amber-200',
text: 'text-amber-700',
badge: 'bg-amber-100 text-amber-800',
label: '🟡 Upgrade in Progress',
description: 'System maintenance underway'
},
partial_outage: {
icon: AlertCircle,
color: 'orange',
bg: 'bg-orange-50',
border: 'border-orange-200',
text: 'text-orange-700',
badge: 'bg-orange-100 text-orange-800',
label: '🟠 Partial Outage',
description: 'Some features are affected'
},
major_outage: {
icon: XCircle,
color: 'red',
bg: 'bg-red-50',
border: 'border-red-200',
text: 'text-red-700',
badge: 'bg-red-100 text-red-800',
label: '🔴 Major Outage',
description: 'Significant service disruption'
}
};

return configs[statusType] || configs.operational;
};

const formatDate = (date) => {
if (!date) return null;
return new Date(date).toLocaleDateString('en-US', {
month: 'long',
day: 'numeric',
year: 'numeric',
hour: '2-digit',
minute: '2-digit',
timeZone: 'UTC',
timeZoneName: 'short'
});
};

const handleGoBack = () => {
// Try to close the current tab
if (window.history.length > 1) {
window.history.back();
} else {
// If opened in new tab, try to close it
window.close();
// If can't close (browser restriction), redirect to home
setTimeout(() => {
window.location.href = '/';
}, 100);
}
};

if (loading) {
return (
<div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50 flex items-center justify-center">
<div className="text-center">
<div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
<p className="text-slate-600 font-medium">Loading system status...</p>
</div>
</div>
);
}

if (!status) {
return (
<div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50 flex items-center justify-center p-4">
<div className="text-center">
<AlertCircle className="text-slate-400 mx-auto mb-4" size={64} />
<p className="text-slate-600 font-medium">Unable to load system status</p>
<button
onClick={loadStatus}
className="mt-4 px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all"
>
Retry
</button>
</div>
</div>
);
}

const config = getStatusConfig(status.status);
const StatusIcon = config.icon;

return (
<div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50">
{/* Back Button - Top Left */}
<div className="fixed top-6 left-6 z-10">
<button
onClick={handleGoBack}
className="flex items-center gap-2 px-4 py-2 bg-white hover:bg-slate-50 text-slate-700 font-medium rounded-lg shadow-md hover:shadow-lg transition-all border border-slate-200"
>
<ArrowLeft size={18} />
Back
</button>
</div>

<div className="max-w-4xl mx-auto px-4 py-12 pt-24">
{/* 1. CLEAR STATUS AT TOP */}
<div className={`${config.bg} border-2 ${config.border} rounded-2xl p-8 mb-6 shadow-lg`}>
<div className="flex items-start gap-4 mb-4">
<div className="p-3 bg-white rounded-xl shadow-sm">
<StatusIcon className={config.text} size={20} />
</div>
<div className="flex-1">
<div className={`inline-block ${config.badge} px-4 py-1.5 rounded-full text-sm font-bold mb-2`}>
{config.label}
</div>
<h1 className="text-md lg:text-3xl font-bold text-slate-900 mb-2">
{status.headline}
</h1>
<p className={`${config.text} font-medium text-sm`}>
{config.description}
</p>
</div>
</div>

{status.estimated_completion && (
<div className="flex items-center gap-2 mt-4 pt-4 border-t border-slate-200 text-sm">
<Calendar className={config.text} size={20} />
<span className={`font-semibold ${config.text} flex flex-col gap-1`}>
<span className="text-xs">Estimated completion:</span> 
<span>{formatDate(status.estimated_completion)}</span>
</span>
</div>
)}
</div>

{/* 2. WHAT'S AFFECTED */}
{(status.affected_features?.length > 0 || status.operational_features?.length > 0) && (
<div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
{/* Affected Features */}
{status.affected_features?.length > 0 && (
<div className="bg-white rounded-xl border border-red-200 p-6 shadow-sm">
<div className="flex items-center gap-2 mb-4">
<AlertCircle className="text-red-600" size={24} />
<h2 className="text-xl font-bold text-slate-900">Currently Affected</h2>
</div>
<ul className="space-y-2">
{status.affected_features?.map((feature, idx) => (
<li key={idx} className="flex items-start gap-2 text-slate-700">
<span className="text-red-500 mt-1">•</span>
<span>{feature}</span>
</li>
))}
</ul>
</div>
)}

{/* Operational Features */}
{status.operational_features?.length > 0 && (
<div className="bg-white rounded-xl border border-green-200 p-6 shadow-sm">
<div className="flex items-center gap-2 mb-4">
<CheckCircle className="text-green-600" size={24} />
<h2 className="text-xl font-bold text-slate-900">Fully Operational</h2>
</div>
<ul className="space-y-2">
{status.operational_features.map((feature, idx) => (
<li key={idx} className="flex items-start gap-2 text-slate-700">
<span className="text-green-500 mt-1">✓</span>
<span>{feature}</span>
</li>
))}
</ul>
</div>
)}
</div>
)}

{/* 3. WHY WE'RE DOING THIS */}
{status.reason && (
<div className="bg-white rounded-xl border border-slate-200 p-6 mb-6 shadow-sm">
<div className="flex items-center gap-2 mb-3">
<Info className="text-blue-600" size={24} />
<h2 className="text-xl font-bold text-slate-900">Why This Upgrade</h2>
</div>
<p className="text-slate-700 leading-relaxed">
{status.reason}
</p>
</div>
)}

{/* 4. COMPENSATION TRANSPARENCY */}
{status.compensation_message && (
<div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-xl p-6 mb-6 shadow-sm">
<div className="flex items-center gap-2 mb-3">
<Shield className="text-purple-600" size={24} />
<h2 className="text-xl font-bold text-slate-900">Compensation</h2>
</div>
<p className="text-slate-700 leading-relaxed">
{status.compensation_message}
</p>
</div>
)}

{/* 5. PROGRESS TIMELINE */}
{status.progress_timeline?.length > 0 && (
<div className="bg-white rounded-xl border border-slate-200 p-6 mb-6 shadow-sm">
<h2 className="text-xl font-bold text-slate-900 mb-4">Progress Timeline</h2>
<div className="space-y-3">
{status.progress_timeline.map((item, idx) => (
<div key={idx} className="flex items-start gap-3">
<div className="flex-shrink-0 mt-1">
<div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center">
<ChevronRight className="text-blue-600" size={16} />
</div>
</div>
<div className="flex-1">
<p className="text-slate-800 font-medium">{item.phase || item.title}</p>
{item.description && (
<p className="text-sm text-slate-600 mt-1">{item.description}</p>
)}
</div>
</div>
))}
</div>
</div>
)}

{/* 6. LAST UPDATED */}
<div className="bg-white rounded-xl border border-slate-200 p-4 mb-6 shadow-sm">
<div className="flex items-center justify-between">
<div className="flex items-center gap-2 text-sm text-slate-600">
<Clock size={16} />
<span>Last updated: {formatDate(status.updated_at)}</span>
</div>
<button
onClick={loadStatus}
className="text-sm text-blue-600 hover:text-blue-700 font-medium"
>
Refresh
</button>
</div>
</div>

{/* 7. NOTIFY ME BUTTON */}
{status.show_notify_button && status.status !== 'operational' && (
<div className="bg-gradient-to-r from-blue-600 to-cyan-600 rounded-xl p-6 shadow-lg">
<div className="flex flex-col md:flex-row items-center justify-between gap-4">
<div className="flex items-start gap-3 text-white">
<Bell size={24} className="flex-shrink-0 mt-1" />
<div>
<h3 className="font-bold text-lg mb-1">Get Notified</h3>
<p className="text-sm text-blue-100">
Receive an email when the upgrade is complete
</p>
</div>
</div>
<button
onClick={() => setShowNotifyModal(true)}
className="px-6 py-3 bg-white hover:bg-blue-50 text-blue-600 font-semibold rounded-lg shadow-md transition-all flex-shrink-0"
>
Notify Me
</button>
</div>
</div>
)}

{/* Trust Badge */}
<div className="mt-8 text-center">
<div className="inline-flex items-center gap-2 text-sm text-slate-500">
<Shield size={16} />
<span>Your data remains safe and secure throughout this process</span>
</div>
</div>
</div>

{/* Notify Modal */}
{showNotifyModal && (
<div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
<div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
{!notifySuccess ? (
<>
<div className="flex items-center justify-between mb-4">
<h3 className="text-xl font-bold text-slate-900">Get Notified</h3>
<button
onClick={() => setShowNotifyModal(false)}
className="p-1 hover:bg-slate-100 rounded-lg transition-all"
>
<X size={20} />
</button>
</div>

<p className="text-slate-600 mb-4">
Enter your email to receive a notification when all systems are back to normal.
</p>

<form onSubmit={handleNotifySubscribe}>
<div className="mb-4">
<label className="block text-sm font-semibold text-slate-700 mb-2">
Email Address
</label>
<div className="relative">
<Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
<input
type="email"
value={notifyEmail}
onChange={(e) => setNotifyEmail(e.target.value)}
placeholder="your@email.com"
required
className="w-full pl-10 pr-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
/>
</div>
</div>

<div className="flex gap-3">
<button
type="button"
onClick={() => setShowNotifyModal(false)}
className="flex-1 px-4 py-3 bg-slate-200 hover:bg-slate-300 text-slate-700 font-semibold rounded-lg transition-all"
>
Cancel
</button>
<button
type="submit"
disabled={subscribing}
className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 disabled:from-blue-400 disabled:to-cyan-400 text-white font-semibold rounded-lg transition-all"
>
{subscribing ? 'Subscribing...' : 'Subscribe'}
</button>
</div>
</form>
</>
) : (
<div className="text-center py-4">
<div className="inline-flex p-4 bg-green-100 rounded-full mb-3">
<CheckCircle className="text-green-600" size={48} />
</div>
<h3 className="text-xl font-bold text-slate-900 mb-2">Subscribed!</h3>
<p className="text-slate-600">
We'll notify you when everything is back to normal.
</p>
</div>
)}
</div>
</div>
)}
</div>
);
};

export default SystemStatus;